/**
 * The branding settings screen's own API client.
 *
 * A sibling of `@/lib/api`, not an addition to it: this feature's scope is
 * `web/src/features/settings/branding/**` only, and `lib/api.ts` is shared
 * by every settings sub-screen the wave adds in parallel — editing it here
 * would be exactly the cross-session collision the scope boundary exists to
 * prevent. `ApiError` is imported rather than redefined, so a caller catches
 * one error type across the whole app.
 */

import { ApiError, type ApiErrorBody } from '@/lib/api'

const BASE = '/api/v1/tenancy/settings/branding'

export interface BrandingSettings {
  display_name: string | null
  timezone: string
  currency: string
  locale: string
  support_email: string | null
  sender_name: string | null
  sender_email: string | null
  has_logo: boolean
  has_favicon: boolean
  footer_text: string
}

export interface UpdateBrandingSettings {
  display_name?: string
  timezone?: string
  currency?: string
  locale?: string
  support_email?: string
  sender_name?: string
  sender_email?: string
}

/** The vocabulary the settings form's selects and upload validation use — see `GET /options`. */
export interface BrandingOptions {
  currencies: string[]
  locales: string[]
  logo_max_bytes: number
  logo_content_types: string[]
  favicon_max_bytes: number
  favicon_content_types: string[]
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
    )
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function uploadAsset(path: string, file: File): Promise<BrandingSettings> {
  return request<BrandingSettings>(path, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })
}

/**
 * The tenant's logo or favicon as an object URL, for an `<img>` or the tab
 * icon — `null` when nothing is set. Not a bare `<img src="/api/...">`,
 * because the image is behind the same auth every other tenant-scoped
 * resource is, and the browser's own tab-icon fetch carries none of that.
 */
async function assetObjectUrl(path: string): Promise<string | null> {
  const response = await fetch(`${BASE}${path}`, { credentials: 'same-origin' })
  if (response.status === 404) return null
  if (!response.ok) throw new ApiError(response.status, 'temporary_failure', response.statusText)
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

export const brandingApi = {
  current: () => request<BrandingSettings>(''),
  options: () => request<BrandingOptions>('/options'),
  update: (payload: UpdateBrandingSettings) =>
    request<BrandingSettings>('', { method: 'PUT', body: JSON.stringify(payload) }),
  uploadLogo: (file: File) => uploadAsset('/logo', file),
  removeLogo: () => request<BrandingSettings>('/logo', { method: 'DELETE' }),
  logoObjectUrl: () => assetObjectUrl('/logo'),
  uploadFavicon: (file: File) => uploadAsset('/favicon', file),
  removeFavicon: () => request<BrandingSettings>('/favicon', { method: 'DELETE' }),
  faviconObjectUrl: () => assetObjectUrl('/favicon'),
}
